import type {
  Product,
  ProductListVariation,
  StockLevel,
  VariationQuantity,
} from '../../../types/product'

export function mapListVariations(
  rawVariations: unknown,
  categories: { id: string | number; name?: string }[],
  parentBrand: unknown,
  resolveStock: (qty: number, threshold?: number) => StockLevel,
): ProductListVariation[] {
  const items = (rawVariations as Record<string, unknown>[]) ?? []
  if (!Array.isArray(items) || items.length === 0) return []

  const catName = new Map(categories.map((c) => [String(c.id), c.name ?? '']))

  return items.map((v) => {
    const vCats = ((v.categories as { id: number | string }[]) ?? [])
      .map((c) => catName.get(String(c.id)) ?? '')
      .filter(Boolean)
    const vStock = (v.stock_quantity as number) ?? 0
    const rawStatus = String(v.status ?? 'draft')
    const status = (
      rawStatus === 'publish' ? 'published' : rawStatus === 'trash' ? 'archived' : rawStatus
    ) as Product['status']
    type RawVarImage = { src?: string; alt?: string; srcset?: Record<string, string> }
    const singularImg = v.image as RawVarImage | null | undefined
    const pluralImgs = v.images as RawVarImage[] | null | undefined
    // Match the product-level precedence: the variation's own `image`, else its `images[0]`.
    const img = singularImg?.src ? singularImg : pluralImgs?.[0]?.src ? pluralImgs[0] : undefined

    return {
      id: String(v.id ?? ''),
      name: String(v.name ?? ''),
      sku: String(v.short_sku ?? ''),
      image: img?.src
        ? { id: '0', src: String(img.src), alt: String(img.alt ?? ''), isFeatured: true, srcset: img.srcset ?? undefined }
        : undefined,
      categoryName: vCats[0] || undefined,
      subCategories: vCats.slice(1),
      brand: v.brand != null ? String(v.brand) : parentBrand ? String(parentBrand) : undefined,
      price: parseFloat(String(v.price ?? '0')) || 0,
      minPrice: v.min_price != null ? parseFloat(String(v.min_price)) : undefined,
      maxPrice: v.max_price != null ? parseFloat(String(v.max_price)) : undefined,
      salePrice: v.sale_price ? parseFloat(String(v.sale_price)) : undefined,
      saleMinPrice: v.sale_min_price != null ? parseFloat(String(v.sale_min_price)) : undefined,
      saleMaxPrice: v.sale_max_price != null ? parseFloat(String(v.sale_max_price)) : undefined,
      stock: resolveStock(vStock),
      stockQty: vStock,
      status,
      date_created_gmt: v.date_created_gmt ? String(v.date_created_gmt) : undefined,
      quantity: (v.quantity as VariationQuantity) ?? undefined,
    }
  })
}
